/**
 * Partner Affection System
 * Similar to AI waifu affection but for real partners
 */

// Affection thresholds for each level
export const AFFECTION_LEVELS = [
  { level: 1, min: 0, title: 'Strangers', emoji: '👋', color: 'text-gray-400' },
  { level: 2, min: 50, title: 'Acquaintances', emoji: '🙂', color: 'text-blue-400' },
  { level: 3, min: 150, title: 'Friends', emoji: '😊', color: 'text-cyan-400' },
  { level: 4, min: 300, title: 'Close Friends', emoji: '🤝', color: 'text-green-400' },
  { level: 5, min: 500, title: 'Besties', emoji: '💚', color: 'text-emerald-400' },
  { level: 6, min: 750, title: 'Sweethearts', emoji: '💕', color: 'text-pink-400' },
  { level: 7, min: 1000, title: 'Lovebirds', emoji: '💗', color: 'text-rose-400' },
  { level: 8, min: 1500, title: 'Soulmates', emoji: '💖', color: 'text-red-400' },
  { level: 9, min: 2000, title: 'Eternal Bond', emoji: '💞', color: 'text-purple-400' },
  { level: 10, min: 3000, title: 'One & Only', emoji: '💝', color: 'text-primary' },
]

// Affection gains for different actions
export const AFFECTION_GAINS = {
  // Emotes
  emoteReceived: 2,
  emoteSent: 1,
  spicyEmoteReceived: 5,
  spicyEmoteSent: 3,

  // Media
  mediaSent: 15,
  mediaReceived: 10,
  mediaViewedOnce: 20,  // Bonus for view-once content

  // Quests
  questCompleted: 10,
  questVerified: 15,
  questAssigned: 2,

  // Daily
  dailyInteraction: 5,
  streakBonus: (streakDays: number) => Math.min(streakDays * 2, 20),

  // Special
  partnershipCreated: 25,
}

/**
 * Get affection level info from affection points
 */
export function getAffectionLevel(affection: number) {
  let level = AFFECTION_LEVELS[0]

  for (let i = AFFECTION_LEVELS.length - 1; i >= 0; i--) {
    if (affection >= AFFECTION_LEVELS[i].min) {
      level = AFFECTION_LEVELS[i]
      break
    }
  }

  // Calculate progress to next level
  const currentIndex = AFFECTION_LEVELS.findIndex((l) => l.level === level.level)
  const nextLevel = AFFECTION_LEVELS[currentIndex + 1]

  let progress = 100
  let pointsToNext = 0

  if (nextLevel) {
    const currentMin = level.min
    const nextMin = nextLevel.min
    const range = nextMin - currentMin
    const currentProgress = affection - currentMin
    progress = Math.min((currentProgress / range) * 100, 100)
    pointsToNext = nextMin - affection
  }

  return {
    ...level,
    affection,
    progress,
    pointsToNext,
    isMaxLevel: !nextLevel,
  }
}

/**
 * Format affection points for display
 */
export function formatAffection(affection: number): string {
  if (affection >= 1000) {
    return `${(affection / 1000).toFixed(1)}k`
  }
  return affection.toString()
}

/**
 * Get relationship milestone messages
 */
export function getMilestoneMessage(newLevel: number, partnerName: string): string {
  const messages: Record<number, string[]> = {
    2: [
      `You and ${partnerName} are getting to know each other!`,
      `${partnerName} remembers your name now!`,
    ],
    3: [
      `You've become friends with ${partnerName}!`,
      `${partnerName} considers you a friend now!`,
    ],
    4: [
      `Your bond with ${partnerName} is growing stronger!`,
      `${partnerName} trusts you more!`,
    ],
    5: [
      `You and ${partnerName} are besties now!`,
      `${partnerName} thinks you're awesome!`,
    ],
    6: [
      `Things are getting sweeter with ${partnerName}~`,
      `${partnerName}'s heart is warming up to you!`,
    ],
    7: [
      `You and ${partnerName} are lovebirds!`,
      `${partnerName} can't stop thinking about you!`,
    ],
    8: [
      `Soulmates! You and ${partnerName} are truly connected!`,
      `${partnerName} feels a deep connection with you!`,
    ],
    9: [
      `Your bond with ${partnerName} transcends everything!`,
      `${partnerName} would do anything for you!`,
    ],
    10: [
      `MAX LEVEL! You and ${partnerName} are One & Only!`,
      `${partnerName} has pledged eternal devotion to you!`,
    ],
  }

  const levelMessages = messages[newLevel]
  if (!levelMessages) return `Your relationship with ${partnerName} improved!`

  return levelMessages[Math.floor(Math.random() * levelMessages.length)]
}
